import { Truck, FileText, Package } from "lucide-react";
import { formatCurrency, formatDate } from "../utils/responseParser";

/**
 * Status badge color helpers for different entity types.
 * Defined before use to avoid ESLint "no-use-before-define" warnings.
 */

export const getShipmentStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-700";
    const s = status.toLowerCase();
    if (s.includes("transit")) return "bg-blue-100 text-blue-700";
    if (s.includes("deliver")) return "bg-green-100 text-green-700";
    if (s.includes("pend")) return "bg-amber-100 text-amber-700";
    if (s.includes("cancel") || s.includes("exception")) return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-700";
};

export const getInvoicePaymentColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-700";
    const s = status.toLowerCase();
    if (s.includes("paid") && !s.includes("partial") && !s.includes("over")) return "bg-green-100 text-green-700";
    if (s.includes("unpaid") || s.includes("overdue")) return "bg-red-100 text-red-700";
    if (s.includes("partial")) return "bg-amber-100 text-amber-700";
    if (s.includes("over")) return "bg-purple-100 text-purple-700";
    return "bg-gray-100 text-gray-700";
};

export const getOrderStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-700";
    const s = status.toLowerCase();
    // Match backend enum: New, Planned, InProgress, Completed, Cancelled
    if (s.includes("complete") || s.includes("fulfilled")) return "bg-green-100 text-green-700";
    if (s.includes("process") || s.includes("progress") || s.includes("inprogress")) return "bg-blue-100 text-blue-700";
    if (s.includes("pend") || s.includes("planned")) return "bg-amber-100 text-amber-700";
    if (s.includes("cancel")) return "bg-red-100 text-red-700";
    if (s.includes("new")) return "bg-purple-100 text-purple-700";
    return "bg-gray-100 text-gray-700";
};

/**
 * Table configurations for different response types.
 * Defines how data should be displayed in the EnhancedDataTable component.
 */
export const TABLE_CONFIGS = {
    "shipment-list": {
        title: "Shipments",
        icon: Truck,
        columns: [
            { key: "shipmentNumber", header: "Shipment #", width: "w-32", bold: true, color: "text-acs-primary-green" },
            {
                key: ["portOfOrigin", "origin"],
                header: "Route",
                width: "w-48",
                formatter: (value, row) => {
                    const dest = row.portOfDestination || row.destination || "-";
                    return `${value || "-"} \u2192 ${dest}`;
                }
            },
            { key: "status", header: "Status", width: "w-28", render: "badge", getBadgeColor: getShipmentStatusColor },
            { key: "pickupDate", header: "Pickup", width: "w-32", formatter: formatDate },
            { key: "carrierName", header: "Carrier", width: "w-36" },
            { key: "totalCharge", header: "Amount", width: "w-28", formatter: (v) => formatCurrency(v), align: "right" }
        ],
        expandable: true,
        expandedView: "shipment"
    },

    "invoice-list": {
        title: "Invoices",
        icon: FileText,
        columns: [
            { key: "invoiceNumber", header: "Invoice #", width: "w-32", bold: true, color: "text-acs-primary-green" },
            { key: "invoiceDate", header: "Date", width: "w-28", formatter: formatDate },
            { key: "total", header: "Amount", width: "w-32", formatter: (v) => formatCurrency(v), align: "right", bold: true },
            { key: "paymentStatus", header: "Payment", width: "w-32", render: "badge", getBadgeColor: getInvoicePaymentColor },
            { key: "billToCompanyName", header: "Bill To", width: "w-48" }
        ],
        expandable: true,
        expandedView: "invoice"
    },

    "order-list": {
        title: "Orders",
        icon: Package,
        columns: [
            { key: "orderNumber", header: "Order #", width: "w-32", bold: true, color: "text-acs-primary-green" },
            {
                key: ["origin", "originLocation"],
                header: "Route",
                width: "w-48",
                formatter: (value, row) => {
                    const dest = row.destination || row.destinationLocation || "-";
                    return `${value || "-"} \u2192 ${dest}`;
                }
            },
            { key: "status", header: "Status", width: "w-28", render: "badge", getBadgeColor: getOrderStatusColor },
            { key: ["totalAmount", "total", "orderTotalAmount"], header: "Total", width: "w-32", formatter: (v) => formatCurrency(v), align: "right", bold: true }
        ],
        expandable: true,
        expandedView: "order"
    },

    "default": {
        title: "Results",
        icon: Package,
        columns: "auto",
        expandable: false
    }
};
